'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { Button } from '@/app/Components/Button';

export interface InstructorSIRSRatingProps {
  instructorName: string;
  courseSubject?: string;
  courseCode?: string;
  showCourseSpecific?: boolean;
  lightText?: boolean;
}

export interface InstructorSIRSStats {
  instructor_name: string;
  avg_teaching_score: number | null;
  avg_quality_score: number | null;
  total_courses: number;
  unique_courses: number;
}

export interface CourseSpecificSIRSStats {
  instructor_name: string;
  course_subject: string;
  course_code: string;
  times_taught: number;
  avg_teaching_score: number | null;
  avg_quality_score: number | null;
}

export interface InstructorSIRSRatingViewProps {
  instructorName: string;
  professorStats: InstructorSIRSStats;
  courseSpecificStats?: CourseSpecificSIRSStats | null;
  showCourseSpecific?: boolean;
  lightText?: boolean;
  onSelect: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

export interface InstructorSIRSRatingStateProps extends Omit<InstructorSIRSRatingViewProps, 'professorStats'> {
  professorStats: InstructorSIRSStats | null;
  loading?: boolean;
  error?: string | null;
}

export function InstructorSIRSRatingView({ instructorName, professorStats, courseSpecificStats, showCourseSpecific = true, lightText = false, onSelect }: InstructorSIRSRatingViewProps) {
  const displayScore = professorStats.avg_teaching_score ?? professorStats.avg_quality_score;
  const primaryText = lightText ? 'text-current' : 'text-[var(--ss-focus)]';
  const secondaryText = lightText ? 'text-current opacity-85' : 'text-[var(--ss-text-muted)]';

  return (
    <div className="space-y-1 text-sm" aria-label={`SIRS rating for ${instructorName}`}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <Button variant="link" size="sm" className={`min-h-12 text-left md:min-h-9 ${primaryText}`} onClick={onSelect}>View SIRS profile for {instructorName}</Button>
        {displayScore !== null ? <p className={secondaryText}><span className="font-semibold tabular-nums" aria-label={`${displayScore.toFixed(1)} out of 5`}>{displayScore.toFixed(1)}/5.0</span>{' · '}SIRS across {professorStats.total_courses} courses</p> : null}
      </div>
      {showCourseSpecific && courseSpecificStats && courseSpecificStats.times_taught > 0 ? (
        <p className={`ml-2 border-l-2 border-current/30 pl-3 ${secondaryText}`}>
          <span className="font-semibold">This course:</span>{' '}
          {courseSpecificStats.avg_teaching_score !== null ? <><span className="tabular-nums">{courseSpecificStats.avg_teaching_score.toFixed(1)}/5.0</span>{courseSpecificStats.times_taught > 1 ? ` (${courseSpecificStats.times_taught} times)` : null}</> : 'N/A'}
        </p>
      ) : null}
    </div>
  );
}

export function InstructorSIRSRatingState({ loading = false, error = null, professorStats, lightText = false, ...viewProps }: InstructorSIRSRatingStateProps) {
  if (loading) return <div className={`text-sm ${lightText ? 'text-current opacity-80' : 'text-[var(--ss-text-muted)]'}`}>Loading SIRS ratings...</div>;
  if (error || !professorStats) return null;
  return <InstructorSIRSRatingView {...viewProps} professorStats={professorStats} lightText={lightText} />;
}

export default function InstructorSIRSRating({ instructorName, courseSubject, courseCode, showCourseSpecific = true, lightText = false }: InstructorSIRSRatingProps) {
  const router = useRouter();
  const [professorStats, setProfessorStats] = useState<InstructorSIRSStats | null>(null);
  const [courseSpecificStats, setCourseSpecificStats] = useState<CourseSpecificSIRSStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSIRSRatings = async () => {
      if (!instructorName) { setLoading(false); return; }
      setLoading(true);
      setError(null);
      try {
        const searchResponse = await fetch(`/api/sirs/search?q=${encodeURIComponent(instructorName)}&type=professor`);
        if (!searchResponse.ok) throw new Error('Failed to search SIRS');
        const searchData = await searchResponse.json();
        const matchingProfessor = (searchData.results?.professors || [])[0] as InstructorSIRSStats | undefined;
        if (matchingProfessor) {
          setProfessorStats(matchingProfessor);
          if (showCourseSpecific && courseSubject && courseCode) {
            try {
              const courseResponse = await fetch(`/api/sirs/courses/${encodeURIComponent(courseSubject)}:${encodeURIComponent(courseCode)}`);
              if (courseResponse.ok) {
                const courseData = await courseResponse.json();
                const professorCourseStats = (courseData.professorStats || []).find((stats: CourseSpecificSIRSStats) => {
                  const candidate = stats.instructor_name.toLowerCase();
                  const requested = instructorName.toLowerCase();
                  return candidate === requested || candidate.includes(requested) || requested.includes(candidate);
                });
                if (professorCourseStats) setCourseSpecificStats(professorCourseStats);
              }
            } catch (caught) {
              console.error('Error fetching course-specific stats:', caught);
            }
          }
        }
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : 'Failed to load ratings');
      } finally {
        setLoading(false);
      }
    };
    fetchSIRSRatings();
  }, [instructorName, courseSubject, courseCode, showCourseSpecific]);

  const handleProfessorClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (professorStats) router.push(`/sirs?professor=${encodeURIComponent(professorStats.instructor_name)}`);
  };
  return <InstructorSIRSRatingState instructorName={instructorName} professorStats={professorStats} courseSpecificStats={courseSpecificStats} showCourseSpecific={showCourseSpecific} lightText={lightText} loading={loading} error={error} onSelect={handleProfessorClick} />;
}
